// The LIB acquisitions console route subtree (console slice — vendors / budget /
// POs / receiving). Code-based routes per DEV-GUIDE-002 §4 — a package-composed
// subtree hand-writes `.lazy()` + `createLazyRoute`. This factory hangs the
// `/library/acquisitions` tree off the portal's root route so router.tsx stays the
// single wiring point (composed ALONGSIDE the other lib subtrees — none touches
// another).
//
// GATING is per-screen (router.tsx's documented choice): the acquisitions LAYOUT
// route re-checks lib:acq via useSession/<Show> before it renders any child — a
// defence-in-depth mirror of the API's rbac('lib:acq') guard on vendors / POs /
// receiving. NB the BUDGET LINES tab writes ride lib:admin at the API (money
// config), so that screen additionally <Can>-gates lib:admin over its write
// affordances — the layout gate (lib:acq) only opens the console. The UI can only
// HIDE; the gateway is the authority (ADR-001 §14.2). Anonymous → /login;
// signed-in-without-the-grant → an explicit denied panel, never a blank Outlet.
//
// WIRED (phase-2 done): the three lib:acq keyset lists (vendors / purchase-orders
// / receiving) carry the DEV-GUIDE-002 §4.1 URL-state contract — a self-healing
// validateSearch + loaderDeps → `void prefetchInfiniteQuery(libraryQueries.<list>Infinite({ limit }))`
// so the first page is on the wire before the screen mounts — WARMED, never
// awaited (audit UX-01): an awaited `ensureQueryData` held the route until the
// API answered and the outlet stayed empty for the whole wait; prefetching starts
// the same fetch (same key, deduped against the screen's read) and returns at
// once, so the screen paints its heading and its table bones from
// `queryState(...).pending()`.
//
// NO `cursor` IN ANY OF THESE URLs (2026-09-22). All three lists are INFINITE
// STREAMS now: the PO register's facets (search / status / vendorId /
// budgetLineId / priority) still live in the URL — a shared link restores exactly
// the slice someone was reading — but the cursor does not, because it no longer
// names a page. It names a position inside one accumulating list, and a link to
// "everything up to cursor X" is a different amount of scrolling for every
// reader. A stale `?cursor=` on an old bookmark simply falls out, which is the
// self-healing contract these validators already had.
// `prefetchInfiniteQuery` warms the FIRST page only (its `pages` default is 1)
// into the same accumulating entry the screen reads, so the reader's scroll
// fetches page two, never the loader.
//
// budget-lines is DELIBERATELY loader-free: its API tier is lib:admin (money
// config), STRICTER than this layout's lib:acq gate — a loader would 403 for a
// lib:acq-only session before the screen's <Can perm="lib:admin"> could render
// the denied panel. So that leaf stays a plain lazy stub and the screen self-
// gates + fetches client-side. create-po (a form) and the PO detail (a param
// route whose screen owns its useQuery) are likewise loader-free.
//
// API-surface reality (frozen seam): there is NO delete and NO GET :id for
// vendors/budget lines (create/update only). Vendor "retire" is a status PATCH
// (active→inactive/blacklisted), budget-line edit prefill is find-in-list (the
// Subjects registry precedent) — never a hard delete.
import { lazyRoute } from "@erp/ui/route-kit";
import { Outlet, createRoute } from "@tanstack/solid-router";
import { libraryQueries } from "../../index.js";
import { panelSearch } from "@erp/ui/side-panel";
import { ConsoleLayout } from "@erp/ui/console-layout";
import { HubGrid } from "@erp/ui/hub-grid";
import { PageHeading } from "@erp/ui/page-heading";
import { MODULE_HUE } from "@erp/ui/category-hue";
import { ShoppingCart } from "lucide-solid";
import type { AnyRoute } from "@tanstack/solid-router";

/* ───────────────────────── keyset URL-state (§4.1) ─────────────────────────
 * The page size is fixed and shared, so the screens import ACQ_PAGE_SIZE and
 * their `useInfiniteQuery` filters object is byte-equal to the loader's (same
 * cache entry, DEV-GUIDE-002 §4.3).
 *
 * `validateCursorSearch` is GONE with the pagers (2026-09-22): the vendors and
 * receiving routes carried a cursor and nothing else beyond their panel, so
 * their list search state is now empty and each validates its panel params
 * alone. A param nobody validates falls out of the search by construction. */
export const ACQ_PAGE_SIZE = 25;

/** PO register facets — every value self-heals to undefined. NO cursor (see the header). */
export interface POSearch {
  search?: string;
  status?: string;
  vendorId?: string;
  budgetLineId?: string;
  priority?: string;
}
export function validatePOSearch(input: Record<string, unknown>): POSearch {
  const str = (v: unknown): string | undefined => (typeof v === "string" && v.length > 0 ? v : undefined);
  return {
    search: str(input.search),
    status: str(input.status),
    vendorId: str(input.vendorId),
    budgetLineId: str(input.budgetLineId),
    priority: str(input.priority),
  };
}

/* ─────────────────────────── panel URL-state (the side-panel contract) ───────
 * A form/viewer is a VIEW, so it lives in the URL: `?panel=<name>&panelId=<id>&
 * panelWide=1`. `panelSearch` heals the trio against a CLOSED set of names per
 * route — a `?panel=banana` (or a stale id with no panel) drops to "no panel"
 * instead of throwing, exactly like the cursor/facet validators above. Absent id
 * ⇒ create mode. The sets are exported so the screens and their tests name the
 * same strings. */
export const VENDOR_PANELS = ["new", "edit"] as const;
export const BUDGET_PANELS = ["new", "edit"] as const;
/** Receiving's panel is a VIEWER (the posted GRN document), never a form. */
export const RECEIVING_PANELS = ["grn"] as const;
/**
 * The PO detail's panel is the APPROVAL TRAIL (modules/audit RecordHistory over
 * entity 'purchase-order') — a reader, never a form, so it carries no panelId:
 * the record is already named by the route param.
 */
export const PO_DETAIL_PANELS = ["history"] as const;

const ACQUISITIONS_LINKS = [
  { to: "/library/acquisitions/purchase-orders", label: "Purchase orders", blurb: "Multi-currency POs + budget guard", id: "acq-nav-pos" },
  { to: "/library/acquisitions/receiving", label: "Receiving", blurb: "Goods receipts (GRN) + accession", id: "acq-nav-receiving" },
  { to: "/library/acquisitions/vendors", label: "Vendors", blurb: "Supplier registry", id: "acq-nav-vendors" },
  { to: "/library/acquisitions/budget-lines", label: "Budget lines", blurb: "What each fund has, and what is committed", id: "acq-nav-budget" },
] as const;

/**
 * The acquisitions shell: gates on lib:acq, then renders the active child.
 * Gating sequence lives in @erp/ui/console-layout; the tab strip is retired in
 * favour of the shell sidebar (module manifest) + header breadcrumbs.
 */
function AcquisitionsLayout() {
  return (
    <ConsoleLayout
      perm="lib:acq"
      deniedMessage="This account cannot open acquisitions. Ask an administrator for acquisitions access."
      skeletonId="acquisitions-skeleton"
      deniedId="acquisitions-denied"
      unreachableId="acquisitions-unreachable"
    >
      <Outlet />
    </ConsoleLayout>
  );
}

/** `/library/acquisitions` index — the landing with cards into each workflow. */
function AcquisitionsLanding() {
  return (
    <div>
      {/* The console's identity capsule (DEV-GUIDE-002 §12.5): every acquisitions
          screen wears seafoam, so the landing they hang off wears it too. */}
      <PageHeading
        icon={ShoppingCart}
        hue={MODULE_HUE.acquisitions}
        title="Acquisitions"
        subtitle="Suppliers, budgets, purchase orders and receiving"
        help={{
          title: "How the library buys",
          children: (
            <p>
              Raise a purchase order against a fund, receive what arrives and accession it into the catalogue.
              Purchase orders and Receiving are the day's work; Vendors and Budget lines are the reference they
              draw on — who the library buys from, and what each fund still has once its open orders are counted.
            </p>
          ),
        }}
      />
      {/* Was a hand-drawn grid of Link > Card > CardHeader with a green
          `StatusBadge label="Open"` for a button — see @erp/ui/hub-grid for
          why both were wrong, and for the one place that shape now lives. */}
      <HubGrid id="acquisitions-landing" items={ACQUISITIONS_LINKS} hue={MODULE_HUE.acquisitions} columns={3} />
    </div>
  );
}

/**
 * Build the `/library/acquisitions` subtree under a portal's root route. Returns
 * the routes to spread into `rootRoute.addChildren([...])` (router.tsx). Static
 * `purchase-orders/new` is registered before the dynamic `purchase-orders/$poId`
 * (path ranking makes static win regardless; the order documents intent).
 */
export function makeLibAcquisitionsRoutes<TParent extends AnyRoute>(rootRoute: TParent) {
  const acquisitionsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "library/acquisitions",
    component: AcquisitionsLayout,
  });

  const indexRoute = createRoute({
    getParentRoute: () => acquisitionsRoute,
    path: "/",
    component: AcquisitionsLanding,
  });

  const purchaseOrdersRoute = createRoute({
    getParentRoute: () => acquisitionsRoute,
    path: "purchase-orders",
    validateSearch: validatePOSearch, // self-healing (§4.1)
    loaderDeps: ({ search }) => ({
      search: search.search,
      status: search.status,
      vendorId: search.vendorId,
      budgetLineId: search.budgetLineId,
      priority: search.priority,
    }),
    loader: ({ context, deps }) => {
      // ONE definition serves loader + screen — the filters object MUST equal the
      // screen's so both hit the same cache entry (§4.3).
      void context.queryClient.prefetchInfiniteQuery(
        libraryQueries.purchaseOrdersInfinite({
          limit: ACQ_PAGE_SIZE,
          search: deps.search,
          status: deps.status,
          vendorId: deps.vendorId,
          budgetLineId: deps.budgetLineId,
          priority: deps.priority,
        }),
      );
    },
  }).lazy(lazyRoute(() => import("../../screens/admin/acquisitions/PurchaseOrders.js")));

  const createPORoute = createRoute({
    getParentRoute: () => acquisitionsRoute,
    path: "purchase-orders/new",
  }).lazy(lazyRoute(() => import("../../screens/admin/acquisitions/CreatePO.js")));

  const purchaseOrderDetailRoute = createRoute({
    getParentRoute: () => acquisitionsRoute,
    path: "purchase-orders/$poId",
    // Loader-free (the screen owns its useQuery), so the URL owns exactly one
    // thing here: the history panel — self-healing like every other panel route.
    validateSearch: (input: Record<string, unknown>) => panelSearch(PO_DETAIL_PANELS, input),
  }).lazy(lazyRoute(() => import("../../screens/admin/acquisitions/PurchaseOrderDetail.js")));

  const receivingRoute = createRoute({
    getParentRoute: () => acquisitionsRoute,
    path: "receiving",
    // The GRN viewer panel is the whole of this route's search state now — the
    // cursor left with the pager (see the header).
    validateSearch: (input: Record<string, unknown>) => panelSearch(RECEIVING_PANELS, input),
    loader: ({ context }) => {
      void context.queryClient.prefetchInfiniteQuery(
        libraryQueries.receivablePurchaseOrdersInfinite({ limit: ACQ_PAGE_SIZE }),
      );
    },
  }).lazy(lazyRoute(() => import("../../screens/admin/acquisitions/Receiving.js")));

  const vendorsRoute = createRoute({
    getParentRoute: () => acquisitionsRoute,
    path: "vendors",
    // The add/edit panel is the whole of this route's search state now — the
    // cursor left with the pager (see the header).
    validateSearch: (input: Record<string, unknown>) => panelSearch(VENDOR_PANELS, input),
    loader: ({ context }) => {
      void context.queryClient.prefetchInfiniteQuery(libraryQueries.vendorsInfinite({ limit: ACQ_PAGE_SIZE }));
    },
  }).lazy(lazyRoute(() => import("../../screens/admin/acquisitions/Vendors.js")));

  const budgetLinesRoute = createRoute({
    getParentRoute: () => acquisitionsRoute,
    path: "budget-lines",
    // Loader-free (the lib:admin gate reason above) and cursor-free (the screen
    // streams it), so the URL owns exactly one thing here: the panel.
    validateSearch: (input: Record<string, unknown>) => panelSearch(BUDGET_PANELS, input),
  }).lazy(lazyRoute(() => import("../../screens/admin/acquisitions/BudgetLines.js")));

  return [
    acquisitionsRoute.addChildren([
      indexRoute,
      purchaseOrdersRoute,
      createPORoute,
      purchaseOrderDetailRoute,
      receivingRoute,
      vendorsRoute,
      budgetLinesRoute,
    ]),
  ];
}
